import type { FileWithPath } from "../../../../hooks/useS3Upload";
import { canonicalMediaKey } from "../../../preview/ma/maTypes";

/**
 * ドロップされたプロジェクトフォルダを RPP / XML / Media に振り分ける。
 *
 * 前提の構成（RPP と XML が同じ階層、参照メディアはその下の Media/）:
 *   <プロジェクト>/
 *     xxx.RPP
 *     Preview.xml
 *     Media/  動画・オーディオ
 */

export interface MediaEntry {
  file: File;
  /** RPP の置き場所からの相対パス（RPP の FILE にそのまま書く。例: "Media/foo.wav"） */
  relativePath: string;
}

export interface ProjectFolder {
  /** RPP の置き場所（ドロップ内の相対ディレクトリ。直下なら空文字） */
  baseDir: string;
  /** baseDir 直下の RPP（書き出し済みの `_relocate` を後ろにし、それぞれ更新日時の新しい順） */
  rppFiles: File[];
  /** baseDir 直下の XML（更新日時の新しい順） */
  xmlFiles: File[];
  /** Media フォルダ名（実際の大文字小文字。見つからない場合は null） */
  mediaDirName: string | null;
  /** canonicalMediaKey(ファイル名) → Media 内のファイル */
  media: Map<string, MediaEntry>;
}

const MEDIA_DIR = "media";
/** REAPER の自動バックアップ置き場 */
const BACKUP_DIR = "backups";

const splitPath = (path: string): string[] =>
  path.replace(/\\/g, "/").split("/").filter(Boolean);

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
};

const newestFirst = (a: File, b: File): number =>
  b.lastModified - a.lastModified;

/** このツールが書き出した RPP（`<元の名前>_relocate.RPP`）の末尾 */
export const RELOCATED_RPP_SUFFIX = "_relocate";

const isRelocatedRpp = (file: File): boolean =>
  file.name.replace(/\.[^.]+$/, "").endsWith(RELOCATED_RPP_SUFFIX);

/**
 * 書き出し済みの RPP を同じフォルダに置いたまま再実行しても二重に移植しない
 * よう、移植前の RPP を先（= 既定の選択）にする
 */
const sourceRppFirst = (a: File, b: File): number =>
  Number(isRelocatedRpp(a)) - Number(isRelocatedRpp(b)) || newestFirst(a, b);

/**
 * 振り分ける。RPP が複数階層にある場合は最も浅い階層（同じ深さなら名前順で先）を
 * プロジェクトの置き場所とみなす（Media 内の素材用 RPP や Backups を拾わない）。
 */
export function classifyProjectFiles(files: FileWithPath[]): ProjectFolder {
  const entries = files
    .map(({ file, relativePath }) => ({
      file,
      parts: splitPath(relativePath || file.name),
    }))
    .filter(
      ({ parts }) =>
        parts.length > 0 && !parts[parts.length - 1].startsWith("."),
    );

  const rppCandidates = entries.filter(
    ({ parts }) =>
      extensionOf(parts[parts.length - 1]) === "rpp" &&
      !parts
        .slice(0, -1)
        .some((p) => [MEDIA_DIR, BACKUP_DIR].includes(p.toLowerCase())),
  );
  const baseParts =
    rppCandidates
      .map(({ parts }) => parts.slice(0, -1))
      .sort(
        (a, b) => a.length - b.length || a.join("/").localeCompare(b.join("/")),
      )[0] ?? [];
  const baseDir = baseParts.join("/");

  const isInBase = (parts: string[]) =>
    parts.length === baseParts.length + 1 &&
    baseParts.every((p, i) => parts[i] === p);

  const rppFiles = entries
    .filter(
      ({ parts }) =>
        isInBase(parts) && extensionOf(parts[parts.length - 1]) === "rpp",
    )
    .map(({ file }) => file)
    .sort(sourceRppFirst);
  const xmlFiles = entries
    .filter(
      ({ parts }) =>
        isInBase(parts) && extensionOf(parts[parts.length - 1]) === "xml",
    )
    .map(({ file }) => file)
    .sort(newestFirst);

  // Media 配下（サブフォルダ含む）。同名ファイルは浅い階層を優先する
  let mediaDirName: string | null = null;
  const mediaEntries = entries
    .filter(
      ({ parts }) =>
        parts.length > baseParts.length + 1 &&
        baseParts.every((p, i) => parts[i] === p) &&
        parts[baseParts.length].toLowerCase() === MEDIA_DIR,
    )
    .sort((a, b) => a.parts.length - b.parts.length);
  const media = new Map<string, MediaEntry>();
  mediaEntries.forEach(({ file, parts }) => {
    mediaDirName ??= parts[baseParts.length];
    const key = canonicalMediaKey(parts[parts.length - 1]);
    if (media.has(key)) return;
    media.set(key, {
      file,
      relativePath: parts.slice(baseParts.length).join("/"),
    });
  });

  return { baseDir, rppFiles, xmlFiles, mediaDirName, media };
}

/** `<input webkitdirectory>` で選ばれたファイルを FileWithPath に揃える */
export const filesFromDirectoryInput = (fileList: FileList): FileWithPath[] =>
  Array.from(fileList).map((file) => ({
    file,
    relativePath: file.webkitRelativePath || file.name,
  }));
